import React, { useEffect, useRef, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useApi } from "../shared/api/api";

const PROFILE_ENDPOINT = "/usuarios.php?action=profile";
const CURRENT_PROFILE_PHOTO_KEY = "profilePhoto:current";

export default function DashboardLayout({ children }: { children: React.ReactNode }) {
  const { apiFetch } = useApi();
  const [darkMode, setDarkMode] = useState(true);
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [fotoPerfil, setFotoPerfil] = useState(() => localStorage.getItem(CURRENT_PROFILE_PHOTO_KEY) || "");
  const [showPhotoPreview, setShowPhotoPreview] = useState(false);
  const navbarRef = useRef<HTMLElement | null>(null);
  const currentYear = new Date().getFullYear();
  const navigate = useNavigate();

  useEffect(() => {
    const savedMode = localStorage.getItem("darkMode");
    if (savedMode) {
      setDarkMode(savedMode === "true");
    }
  }, []);

  useEffect(() => {
    localStorage.setItem("darkMode", String(darkMode));
  }, [darkMode]);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 16);
    };

    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    if (!menuOpen) return undefined;

    const handlePointerDown = (event: PointerEvent) => {
      if (navbarRef.current?.contains(event.target as Node)) return;
      setMenuOpen(false);
    };

    document.addEventListener("pointerdown", handlePointerDown);
    return () => document.removeEventListener("pointerdown", handlePointerDown);
  }, [menuOpen]);

  useEffect(() => {
    let activo = true;

    const getCampo = (obj: Record<string, unknown> | null, nombres: string[]) => {
      if (!obj) return "";
      const nombre = nombres.find((key) => obj[key] !== undefined && obj[key] !== null);
      return nombre ? String(obj[nombre]) : "";
    };

    const cargarPerfil = async () => {
      const data = await apiFetch(PROFILE_ENDPOINT);
      if (!activo || !data) return;

      const perfil = data as Record<string, unknown>;
      const fotoServidor = getCampo(perfil, ["Foto", "foto", "Imagen", "imagen", "Avatar", "avatar", "FotoPerfil", "fotoPerfil"]);
      const fotoActual = fotoServidor || "";

      setFotoPerfil(fotoActual);
      if (fotoActual) localStorage.setItem(CURRENT_PROFILE_PHOTO_KEY, fotoActual);
      if (!fotoActual) localStorage.removeItem(CURRENT_PROFILE_PHOTO_KEY);
    };

    cargarPerfil();

    return () => {
      activo = false;
    };
  }, [apiFetch]);

  useEffect(() => {
    const actualizarFoto = (event: Event) => {
      const customEvent = event as CustomEvent<{ photo?: string }>;
      setFotoPerfil(customEvent.detail?.photo || "");
    };

    window.addEventListener("profile-photo-updated", actualizarFoto);
    return () => window.removeEventListener("profile-photo-updated", actualizarFoto);
  }, []);

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem(CURRENT_PROFILE_PHOTO_KEY);
    navigate("/");
  };

  const closeMenu = () => {
    setMenuOpen(false);
  };

  const getNavLinkClass = ({ isActive }: { isActive: boolean }) =>
    `dashboard-nav-link ${isActive ? "active" : ""}`;

  return (
    <div className={`dashboard-shell min-vh-100 ${darkMode ? "dashboard-dark" : "dashboard-light"}`}>
      <nav ref={navbarRef} className={`navbar navbar-expand-lg dashboard-navbar ${scrolled ? "dashboard-navbar-scrolled" : ""} ${menuOpen ? "dashboard-navbar-menu-open" : ""}`}>
        <div className="container-fluid">
          <div className="dashboard-brand">
            <button
              type="button"
              className="dashboard-brand-mark dashboard-brand-photo dashboard-brand-photo-button"
              onClick={() => fotoPerfil && setShowPhotoPreview(true)}
              disabled={!fotoPerfil}
              aria-label={fotoPerfil ? "Ver foto de perfil" : "Sin foto de perfil"}
            >
              {fotoPerfil ? <img src={fotoPerfil} alt="Foto de perfil" /> : "A"}
            </button>
            <Link className="dashboard-brand-text" to="/citas">
              <strong>Agenda</strong>
              <small>Control de citas</small>
            </Link>
          </div>

          <button
            className="navbar-toggler dashboard-toggler"
            type="button"
            aria-controls="dashboardNavbar"
            aria-expanded={menuOpen}
            aria-label="Toggle navigation"
            onClick={() => setMenuOpen((open) => !open)}
          >
            <span className="navbar-toggler-icon"></span>
          </button>

          <div className={`collapse navbar-collapse ${menuOpen ? "show" : ""}`} id="dashboardNavbar">
            <ul className="navbar-nav dashboard-nav me-auto mb-2 mb-lg-0">
              <li className="nav-item">
                <NavLink className={getNavLinkClass} to="/agenda" onClick={closeMenu}>Agenda</NavLink>
              </li>
              <li className="nav-item">
                <NavLink className={getNavLinkClass} to="/citas" onClick={closeMenu}>Citas</NavLink>
              </li>
              <li className="nav-item">
                <NavLink className={getNavLinkClass} to="/administrar" onClick={closeMenu}>Administrar</NavLink>
              </li>
              <li className="nav-item">
                <NavLink
                  className={getNavLinkClass}
                  to="/perfil"
                  onClick={closeMenu}
                >
                  Mi Perfil
                </NavLink>
              </li>
            </ul>

            <div className="dashboard-actions">
              <button className="btn btn-sm dashboard-mode-btn" onClick={() => setDarkMode(!darkMode)}>
                {darkMode ? "Modo Claro" : "Modo Oscuro"}
              </button>
              <button className="btn btn-sm dashboard-logout-btn" onClick={handleLogout}>
                Cerrar Sesion
              </button>
            </div>
          </div>
        </div>
      </nav>

      <main className="container-fluid p-0 p-md-3 dashboard-main">
        {children}
      </main>

      <footer className="dashboard-footer">
        <span>&copy; Agenda - Control de citas</span>
        <span>{currentYear}</span>
      </footer>

      {showPhotoPreview && fotoPerfil && (
        <div className="modal fade show d-block" tabIndex={-1} role="dialog" style={{ backgroundColor: "rgba(0,0,0,0.62)" }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content profile-photo-preview-modal">
              <div className="modal-header">
                <h5 className="modal-title">Foto de perfil</h5>
                <button type="button" className="btn-close" onClick={() => setShowPhotoPreview(false)}></button>
              </div>
              <div className="modal-body">
                <img src={fotoPerfil} alt="Foto de perfil ampliada" className="profile-photo-preview-image" />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
